





















import Vue from "vue";
import {
  WordSelectExampleSentence as WordSelectExampleSentenceInterface,
  WordSelectOptions as WordSelectOptionsInterface,
  WordSelectSentence as WordSelectSentenceInterface
} from "@tt-app/types";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import AudioElement from "@tt-app/components/exercise/common/AudioElement.vue";
import WordSelectExampleSentence from "@tt-app/components/exercise/wordSelect/WordSelectExampleSentence.vue";
import WordSelectSentence from "@tt-app/components/exercise/wordSelect/WordSelectSentence.vue";
import WordSelectOptions from "@tt-app/components/exercise/wordSelect/WordSelectOptions.vue";

interface Selections {
  [key: number]: (
    | {
        index: number;
        option: any;
      }
    | undefined
  )[];
}

interface Data {
  selections: Selections;
}

interface Methods {
  dynamicComponent: (componentDefinition: any) => any;
  wordDeselectionHandler: (item: any) => any;
  wordSelectionHandler: (item: any, blankId: any) => any;
}

interface Computed {
  blankCount: any;
  complete: any;
  components: any[];
  selectionCount: any;
  sentence: WordSelectSentenceInterface;
}

interface Props {
  audioDecoderPlayer: AudioDecoderPlayer;
  body: (
    | WordSelectExampleSentenceInterface
    | WordSelectSentenceInterface
    | WordSelectOptionsInterface
  )[];
  header: any;
  problemUuid: string;
  totalQuestions: number;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "WordSelectQuestionCard",
  components: {
    AudioElement,
    WordSelectExampleSentence,
    WordSelectOptions,
    WordSelectSentence
  },
  props: {
    audioDecoderPlayer: {
      type: AudioDecoderPlayer,
      required: true
    },
    body: {
      type: Array,
      default: () => []
    },
    header: {
      type: Array,
      default: () => [""]
    },
    problemUuid: {
      type: String,
      required: true
    },
    totalQuestions: {
      type: Number,
      required: true
    }
  },
  data() {
    const component = this.body.find(component => {
      return component.type === "WORD_SELECT_SENTENCE";
    });

    const blanks = (component as WordSelectSentenceInterface).structure.filter(element => {
      return element.type === "BLANK";
    });

    const selections = blanks.reduce((previousValue, blank) => {
      const newValue: {
        [key: number]: undefined[];
      } = { ...previousValue };

      if (blank.id) {
        newValue[blank.id] = [undefined];

        return newValue;
      } else {
        if (!newValue[0]) {
          newValue[0] = [undefined];
        } else {
          newValue[0].push(undefined);
        }

        return newValue;
      }
    }, {});

    return {
      selections
    };
  },
  computed: {
    blankCount() {
      const blanks = this.sentence.structure.filter(element => {
        return element.type === "BLANK";
      });

      return blanks.length;
    },

    complete() {
      return this.blankCount === this.selectionCount;
    },

    components() {
      return this.body;
    },

    selectionCount() {
      return Object.values(this.selections).reduce((previousValue, currentArray) => {
        return previousValue + currentArray.filter(Boolean).length;
      }, 0);
    },

    sentence() {
      return this.components.find(component => {
        return component.type === "WORD_SELECT_SENTENCE";
      });
    }
  },
  methods: {
    dynamicComponent(componentDefinition) {
      switch (componentDefinition.type) {
        case "AUDIO":
          return "audio-element";
        case "PLAIN_TEXT":
          return "word-select-example-sentence";
        case "WORD_SELECT_SENTENCE":
          return "word-select-sentence";
        case "WORD_SELECT_OPTIONS":
          return "word-select-options";
        default:
          return "";
      }
    },
    wordDeselectionHandler(item) {
      if (item.id) {
        const newSelections = { ...this.selections };

        newSelections[item.id][0] = undefined;

        this.selections = newSelections;
      } else {
        const newSelections = { ...this.selections };

        const itemIndex = newSelections[0].findIndex(selection => {
          if (!selection) return false;
          return selection.index === item.index;
        });

        newSelections[0][itemIndex] = undefined;

        this.selections = newSelections;
      }
    },
    wordSelectionHandler(item, blankId) {
      if (blankId) {
        const newSelections = { ...this.selections };

        newSelections[blankId][0] = item;

        this.selections = newSelections;
      } else {
        const newSelections = { ...this.selections };

        const firstEmptyBlank = newSelections[0].findIndex(selection => {
          return selection === undefined;
        });

        newSelections[0][firstEmptyBlank] = item;

        this.selections = newSelections;
      }
    }
  },
  watch: {
    complete(complete) {
      const payload: {
        problemUuid: string;
        selections: any;
        status?: string;
      } = {
        problemUuid: this.problemUuid,
        selections: this.selections,
        status: undefined
      };

      if (complete === true) {
        payload.status = "complete";
      } else {
        payload.status = "incomplete";
      }

      this.$emit("status-change", payload);
    }
  }
});
